import { useState, useRef, useEffect } from "react";
import { Stack, TextField } from "@fluentui/react";
import styles from "./QuestionInput.module.css";
import { useTexts } from "../../context/TextProvider";

interface Props {
    onSend: (question: string, id?: string) => void;
    disabled: boolean;
    placeholder?: string;
    clearOnSend?: boolean;
    conversationId?: string;
    onOpenUsageDisclaimer?: () => void;
    onConsent?: () => void;
    showEyecatcher?: boolean;
    hasMessages?: boolean;
}

export const QuestionInput = ({
    onSend,
    disabled,
    placeholder,
    clearOnSend,
    conversationId,
    onOpenUsageDisclaimer,
    onConsent,
    showEyecatcher = false,
    hasMessages = false,
}: Props) => {
    const [question, setQuestion] = useState<string>("");
    const [hasConsent, setHasConsent] = useState<boolean>(false);
    const [consentActive, setConsentActive] = useState<boolean>(false);
    const [rippleActive, setRippleActive] = useState<boolean>(false);
    const [placeholderIndex, setPlaceholderIndex] = useState(0);
    const [placeholderVisible, setPlaceholderVisible] = useState(true);
    const shuffledPlaceholders = useRef<string[]>([]);
    const textareaWrapperRef = useRef<HTMLDivElement>(null);
    const { texts } = useTexts();

    // preventScroll avoids scrolling the parent page when embedded in an iframe.
    const focusTextarea = () => {
        textareaWrapperRef.current?.querySelector('textarea')?.focus({ preventScroll: true });
    };

    useEffect(() => {
        const textarea = textareaWrapperRef.current?.querySelector('textarea');
        if (!textarea) return;
        textarea.style.height = '0';
        textarea.style.height = `${Math.max(hasMessages ? 24 : 70, textarea.scrollHeight)}px`;
    }, [question, hasConsent, hasMessages]);

    const sendQuestion = () => {
        if (disabled || !question.trim()) {
            if (hasConsent) focusTextarea();
            return;
        }

        if (conversationId) {
            onSend(question, conversationId);
        } else {
            onSend(question);
        }

        if (clearOnSend) {
            setQuestion("");
        }
    };

    const onEnterPress = (ev: React.KeyboardEvent<Element>) => {
        if (ev.key === "Enter" && !ev.shiftKey) {
            ev.preventDefault();
            sendQuestion();
        }
    };

    const onQuestionChange = (
        _ev: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
        newValue?: string
    ) => {
        setQuestion(newValue ?? "");
    };
    useEffect(() => {
        if (hasConsent) {
            focusTextarea();
        }
    }, [hasConsent]);

    useEffect(() => {
        if (!hasConsent || hasMessages) return;
        const textsArr = texts?.texts.chat.placeholderMultilang;
        if (!textsArr?.length) return;

        const rest = [...textsArr.slice(1)].sort(() => Math.random() - 0.5);
        shuffledPlaceholders.current = [textsArr[0], ...rest];

        const interval = setInterval(() => {
            setPlaceholderVisible(false);
            setTimeout(() => {
                setPlaceholderIndex(i => (i + 1) % shuffledPlaceholders.current.length);
                setPlaceholderVisible(true);
            }, 300);
        }, 4000);

        return () => clearInterval(interval);
    }, [hasConsent, hasMessages, texts]);

    const handleConsentClick = () => {
        setConsentActive(true);
        setTimeout(() => {
            setHasConsent(true);
            setConsentActive(false);
            onConsent?.();
        }, 700);
    };

    const sendQuestionDisabled = disabled || !question.trim();
    const animatedPlaceholder = hasMessages ? placeholder : (shuffledPlaceholders.current[placeholderIndex] ?? placeholder);

    return (
        <Stack className={styles.questionInputFooterContainer}>
            {showEyecatcher && (
                <Stack className={styles.chatInputEyecatcher}>
                    {texts?.texts.start.eyecatcherMessage ?? "Neu: Informationen zur Grundsicherung für Arbeitssuchende ab dem 01.07.2026"}
                </Stack>
            )}
            <Stack
                className={
                    hasConsent
                        ? styles.questionInputContainer
                        : styles.questionInputContainerDisabled
                }
                onClick={!hasConsent ? () => setRippleActive(true) : undefined}
            >
                <div ref={textareaWrapperRef} className={styles.questionInputTextAreaWrapper}>
                    <TextField
                        id={styles["question"]}
                        className={styles.questionInputTextArea}
                        multiline
                        rows={1}
                        resizable={false}
                        borderless
                        value={question}
                        onChange={onQuestionChange}
                        onKeyDown={onEnterPress}
                        disabled={!hasConsent}
                        styles={!hasConsent ? { root: { visibility: 'hidden' } } : undefined}
                    />
                    {hasConsent && !question && (
                        <div
                            className={`${styles.fakePlaceholder}${placeholderVisible ? ` ${styles.fakePlaceholderVisible}` : ''}`}
                            aria-hidden="true"
                        >
                            {animatedPlaceholder}
                        </div>
                    )}
                    {!hasConsent && (
                        <div className={styles.consentHint}>
                            <a
                                href={texts?.texts.navigation.dataPrivacyUrl ?? "http://www.das-steht-dir-zu.de/impressum/datenschutz"}
                                target="_blank"
                                rel="noreferrer"
                                aria-label={`${texts?.texts.navigation.dataPrivacy ?? "Datenschutz"} (öffnet neues Fenster)`}
                                onClick={e => e.stopPropagation()}
                            >
                                {texts?.texts.navigation.dataPrivacy ?? "Datenschutz"}
                            </a>{" "}&{" "}
                            <a href="#" onClick={(e) => { e.stopPropagation(); onOpenUsageDisclaimer?.(); }} className={styles.disclaimerBtn}>
                                {texts?.texts.navigation.usageConsent ?? "Nutzungsbedingungen"}
                            </a>{" "}
                            akzeptieren und Chat starten.
                        </div>
                    )}
                </div>
                <div
                    className={styles.questionInputSendButtonContainer}
                    role="button"
                    tabIndex={0}
                    aria-label={texts?.texts.chat.sendButtonText ?? "Frage an den KI-Chat senden"}
                    aria-disabled={sendQuestionDisabled}
                    onClick={sendQuestion}
                    onKeyDown={(e) =>
                        e.key === "Enter" || e.key === " "
                            ? sendQuestion()
                            : null
                    }
                >
                    <div className={styles.questionInputSendButtonWrapper}>
                        <div
                            className={sendQuestionDisabled ? styles.questionInputSendButtonDisabled : styles.questionInputSendButton}
                        />
                        {!hasConsent && (
                            <>
                                <button
                                    className={`${styles.consentCheckButton}${consentActive ? ` ${styles.consentCheckButtonActive}` : ''}`}
                                    onClick={(e) => { e.stopPropagation(); if (!consentActive) handleConsentClick(); }}
                                    aria-label={texts?.texts.chat.consentButtonText ?? "Datenschutz und Nutzungsbedingungen zustimmen"}
                                    disabled={consentActive}
                                />
                                {rippleActive && (
                                    <span
                                        className={styles.consentRippleRing}
                                        onAnimationEnd={() => setRippleActive(false)}
                                    />
                                )}
                            </>
                        )}
                    </div>
                </div>
            </Stack>
        </Stack>
    );
};
